import { useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getItems,
  getItemCount,
  getItemDetail,
  triggerItemAiClassification,
  getRelatedItems,
  bulkUpdateItems,
  updateItem,
  updateItemEnforced,
  updateItemHidden,
  addBookmarkItems,
  removeBookmarkItems,
  postManualItem,
  uploadManualItemImage,
  bulkPostItems,
  reorderItemImage,
  deleteItemImage,
  scheduleItemScreenshot,
  createManualItemScreenshot,
  deleteItemScreenshot,
  scheduleCompliancyCheck,
  getItemCompliancyChecks,
  scheduleAiRecrawl,
  scheduleRecrawl,
  setItemIdle,
  setItemDomain,
  setItemNtdReady,
  setItemManualNtd,
  setItemManualNtdReminder,
  setItemManualNtdUnsuccessful,
  setItemGenuine,
  setItemFlagged,
  setItemCompliant,
  setItemRemoved,
  setItemMemberReview,
  setItemRelisted,
  setItemMemberInfringing,
  setItemMemberCompliant,
  setItemMemberInfringingNoNtd,
  setItemMemberUncertain,
  setItemNonInfringing,
  setItemAnnotatedInfringing,
  setItemUnderInvestigation,
  setItemUnderInvestigationBulk,
  bulkSetItemMemberReview,
  bulkSetItemRemoved,
  bulkSetItemCompliant,
  bulkSetItemNonInfringing,
  bulkSetItemManualNtdReminder,
  editEmailNoticePreview,
  getItemComments,
  sendItemComment,
  updateItemComment,
  deleteItemComment,
  createSavedSearch,
  getSavedSearch,
  listSavedSearches,
  addItemIncomingLinks,
  updateItemKeywords,
  createGroupTag,
  updateGroupTag,
  deleteGroupTag,
  getGroupTags,
  computeImageHash,
  getMemberReviewStatus,
} from '../api/react/items/Items'
import handleErrorToast from '../components/common/toast/handleErrorToast'
import type { ItemFilters, CreateItemPayload, BulkUpdateItemsPayload, UpdateItemPayload, UploadItemImagePayload, BulkCreateItemPayload, BulkItemIds, BulkSetStatusPayload, BulkSetNtdReadyPayload, BulkSetNonInfringingPayload, BulkSetAnnotatedPayload, CreateInvestigationPayload, BulkCreateInvestigationPayload, SavedSearchesFilters } from '../types/items.types'

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const ITEM_KEYS = {
  list: (filters?: ItemFilters) => ['items', filters] as const,
  detail: (id?: string) => ['item', id] as const,
  related: (id?: string) => ['item-related-items', id] as const,
  compliancyChecks: (id: string) => ['getItemCompliancyChecks', id] as const,
  comments: (id: string, pageSize?: number) => pageSize !== undefined ? ['itemComments', id, pageSize] as const : ['itemComments', id] as const,
  savedSearch: (id: string) => ['savedSearch', id] as const,
  savedSearches: (filters?: SavedSearchesFilters) => ['savedSearches', filters] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export const useItems = (filters: ItemFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...ITEM_KEYS.list(filters), 'paginated'],
    queryFn: () => getItems(filters),
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  })
}

export const useItemsInfinite = (
  filters: Omit<ItemFilters, 'page'>,
  options?: { refetchOnWindowFocus?: boolean; enabled?: boolean }
) => {
  return useInfiniteQuery({
    queryKey: [...ITEM_KEYS.list(filters), 'infinite'],
    queryFn: ({ pageParam = 1 }) => getItems({ ...filters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => lastPage?.hasNext ? (lastPageParam as number) + 1 : undefined,
    ...options,
  })
}

export const useItem = (
  id: string | undefined,
  options?: { expand?: string; refetchInterval?: number | false }
) => {
  return useQuery({
    queryKey: [...ITEM_KEYS.detail(id), options?.expand],
    queryFn: () =>
      getItemDetail({
        id,
        expand: options?.expand,
      }),
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
  })
}

export const usePrefetchItem = () => {
  const qc = useQueryClient()
  return (id: string | undefined, expand?: string) => {
    if (!id) return
    qc.prefetchQuery({
      queryKey: [...ITEM_KEYS.detail(id), expand],
      queryFn: () => getItemDetail({ id, expand }),
      staleTime: 30_000,
    })
  }
}

export const useTriggerAiClassification = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => triggerItemAiClassification(itemId),
    onSuccess: (_data, itemId) => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(itemId) })
    },
  })
}

export const useItemsDetails = (ids: string[]) => {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ITEM_KEYS.detail(id),
      queryFn: () => getItemDetail({ id }),
      enabled: !!id,
    })),
  })
}

export const useSavedSearch = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ITEM_KEYS.savedSearch(id),
    queryFn: () => getSavedSearch(id),
    enabled: enabled && !!id,
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export const useCreateItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateItemPayload) => postManualItem(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useBulkCreateItems = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: CreateItemPayload[] | BulkCreateItemPayload[]) => bulkPostItems(items),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useUploadItemImage = () => {
  return useMutation({
    mutationFn: (data: UploadItemImagePayload) => uploadManualItemImage(data),
  })
}

export const useUpdateItem = () => {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (data: BulkUpdateItemsPayload) => bulkUpdateItems(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      if (variables.ids?.length === 1) {
        qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.ids[0]), exact: false })
      }
    },
    onError: () => handleErrorToast(toast, 'Update Failed', 'Could not save the change. Please try again.'),
  })
}

export const useBookmarkItem = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => addBookmarkItems({ item_ids: [id] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) })
    },
  })
}

export const useUnbookmarkItem = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => removeBookmarkItems({ item_ids: [id] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) })
    },
  })
}

export const useCreateSavedSearch = () => {
  return useMutation({
    mutationFn: (data: { table: string; filter_data: Record<string, unknown>; calculate_count: boolean; name?: string }) =>
      createSavedSearch(data),
  })
}

export const useRelatedItems = (
  id: string | undefined,
  options?: { fields?: string; expand?: string }
) => {
  return useQuery({
    queryKey: ITEM_KEYS.related(id),
    queryFn: () => getRelatedItems({ id, fields: options?.fields, expand: options?.expand }),
    enabled: !!id,
  })
}

export const useItemCompliancyChecks = (id: string) => {
  return useQuery({
    queryKey: ITEM_KEYS.compliancyChecks(id),
    queryFn: () => getItemCompliancyChecks(id),
    enabled: !!id,
  })
}

export const useItemComments = (itemId: string, pageSize = 20) => {
  return useInfiniteQuery({
    queryKey: ITEM_KEYS.comments(itemId, pageSize),
    queryFn: ({ pageParam = 1 }) =>
      getItemComments({ item_id: itemId, page: pageParam as number, page_size: pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.totalPages > (lastPageParam as number) ? (lastPageParam as number) + 1 : undefined,
    enabled: !!itemId,
  })
}

export const useSendItemComment = (itemId: string) => {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (comment: string) => sendItemComment({ item_id: itemId, comment }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.comments(itemId) })
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(itemId) })
      qc.invalidateQueries({ queryKey: ['items'] })
    },
    onError: () => handleErrorToast(toast, 'Comment Failed', 'Could not send your comment. Please try again.'),
  })
}

export const useUpdateItemComment = (itemId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { comment_id: string; comment: string }) =>
      updateItemComment({ item_id: itemId, ...data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEM_KEYS.comments(itemId) }),
  })
}

export const useDeleteItemComment = (itemId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (comment_id: string) => deleteItemComment({ item_id: itemId, comment_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEM_KEYS.comments(itemId) }),
  })
}

export const useUpdateItemSingle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateItemPayload }) => updateItem(id, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) })
    },
  })
}

export const useUpdateItemEnforced = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enforced }: { id: string; enforced: boolean }) =>
      updateItemEnforced(id, { enforced }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) })
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useUpdateItemHidden = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, irrelevant }: { id: string; irrelevant: boolean }) =>
      updateItemHidden(id, { irrelevant }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) })
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useReorderItemImage = (itemId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { from_index: number; to_index: number }) =>
      reorderItemImage({ item_id: itemId, ...data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(itemId) }),
  })
}

export const useDeleteItemImage = (itemId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (image_index: number) => deleteItemImage({ item_id: itemId, image_index }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(itemId) }),
  })
}

export const useScheduleScreenshot = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => scheduleItemScreenshot({ id }),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }),
  })
}

export const useCreateManualScreenshot = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; f: File }) => createManualItemScreenshot(data),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }),
  })
}

export const useDeleteScreenshot = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; i: number | null }) => deleteItemScreenshot(data),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }),
  })
}

export const useScheduleCompliancyCheck = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (item_ids: string[]) => scheduleCompliancyCheck(item_ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useScheduleAiRecrawl = () => {
  return useMutation({
    mutationFn: (item_ids: string[]) => scheduleAiRecrawl(item_ids),
  })
}

export const useScheduleRecrawl = () => {
  return useMutation({
    mutationFn: (item_ids: string[]) => scheduleRecrawl({ item_ids }),
  })
}

export const useSetItemIdle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { item_ids: string[] }) => setItemIdle(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; domain: string }) => setItemDomain(data),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }),
  })
}

export const useSetItemNtdReady = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetNtdReadyPayload) => setItemNtdReady(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      variables.item_ids?.forEach((id) =>
        qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) })
      )
    },
  })
}

export const useSetItemManualNtd = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemManualNtd(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemManualNtdReminder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemManualNtdReminder(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemManualNtdUnsuccessful = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemManualNtdUnsuccessful(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemFlagged = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { item_ids: string[] }) => setItemFlagged(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemCompliant = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => setItemCompliant({ id }),
    onSuccess: (_data, id) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useSetItemRemoved = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => setItemRemoved({ id }),
    onSuccess: (_data, id) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useSetItemMemberReview = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => setItemMemberReview({ id }),
    onSuccess: (_data, id) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useSetItemRelisted = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemRelisted(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemMemberInfringing = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemMemberInfringing(data),
    onSuccess: (_data, variables) => {
      variables.item_ids.forEach((id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }))
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useSetItemMemberCompliant = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemMemberCompliant(data),
    onSuccess: (_data, variables) => {
      variables.item_ids.forEach((id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }))
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useSetItemMemberInfringingNoNtd = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemMemberInfringingNoNtd(data),
    onSuccess: (_data, variables) => {
      variables.item_ids.forEach((id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }))
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useSetItemMemberUncertain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetStatusPayload) => setItemMemberUncertain(data),
    onSuccess: (_data, variables) => {
      variables.item_ids.forEach((id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }))
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useSetItemNonInfringing = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; hide_item: boolean }) => setItemNonInfringing(data),
    onSuccess: (_data, variables) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useSetItemAnnotatedInfringing = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSetAnnotatedPayload) => setItemAnnotatedInfringing(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  })
}

export const useSetItemGenuine = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string }) => setItemGenuine(data),
    onSuccess: (_data, variables) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useSetItemUnderInvestigation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInvestigationPayload) => setItemUnderInvestigation(data),
    onSuccess: (_data, variables) => { qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.item_id) }); qc.invalidateQueries({ queryKey: ['items'] }) },
  })
}

export const useAddItemIncomingLinks = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => addItemIncomingLinks(id),
    onSuccess: (_data, id) => qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(id) }),
  })
}

export const useUpdateItemKeywords = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; keywords: string[]; metadata?: Record<string, unknown> }) =>
      updateItemKeywords(data),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ITEM_KEYS.detail(variables.id) }),
  })
}

export const useEditEmailNoticePreview = () => {
  return useMutation({
    mutationFn: (data: { body: string; id: string }) => editEmailNoticePreview(data),
  })
}

export const useGetGroupTags = (params: { search?: string; page?: number; page_size?: number }, enabled = true) => {
  return useQuery({
    queryKey: ['groupTags', params],
    queryFn: () => getGroupTags(params),
    enabled,
    staleTime: 60_000,
  })
}

export const useGetGroupTagsInfinite = (params: { search?: string; page_size?: number }, enabled = true) => {
  return useInfiniteQuery({
    queryKey: ['groupTagsInfinite', params],
    queryFn: ({ pageParam = 1 }) => getGroupTags({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const loaded = allPages.length * (params.page_size ?? 10)
      return loaded < lastPage.count ? (lastPageParam as number) + 1 : undefined
    },
    enabled,
    staleTime: 60_000,
  })
}

export const useCreateGroupTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) => createGroupTag(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['groupTags'] }); qc.invalidateQueries({ queryKey: ['groupTagsInfinite'] }) },
  })
}

export const useUpdateGroupTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; name?: string; description?: string }) => updateGroupTag(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['groupTags'] }); qc.invalidateQueries({ queryKey: ['groupTagsInfinite'] }) },
  })
}

export const useDeleteGroupTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteGroupTag(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['groupTags'] }); qc.invalidateQueries({ queryKey: ['groupTagsInfinite'] }) },
  })
}

export const useComputeImageHash = () => {
  return useMutation({
    mutationFn: (file: File) => computeImageHash(file),
  })
}

export const useMemberReviewStatus = (enabled = true) => {
  return useQuery({
    queryKey: ['memberReviewStatus'],
    queryFn: () => getMemberReviewStatus(),
    enabled,
  })
}

export const useItemCount = (filters: Parameters<typeof getItemCount>[0] = {}) => {
  return useQuery({
    queryKey: ['itemCount', filters],
    queryFn: () => getItemCount(filters),
    staleTime: 60_000,
  })
}

export const useListSavedSearches = (filters: SavedSearchesFilters, enabled = true) => {
  return useQuery({
    queryKey: ITEM_KEYS.savedSearches(filters),
    queryFn: () => listSavedSearches(filters),
    enabled,
  })
}

// Bulk-only hooks: no per-call invalidation — callers (BulkSelectPortal) invalidate once at the end
export const useBulkUpdateItems = () => {
  return useMutation({
    mutationFn: (data: BulkUpdateItemsPayload) => bulkUpdateItems(data),
  })
}

export const useBulkSetItemMemberReview = () => {
  return useMutation({
    mutationFn: (data: BulkItemIds) => bulkSetItemMemberReview(data),
  })
}

export const useBulkSetItemRemoved = () => {
  return useMutation({
    mutationFn: (data: BulkItemIds) => bulkSetItemRemoved(data),
  })
}

export const useBulkSetItemCompliant = () => {
  return useMutation({
    mutationFn: (data: BulkItemIds) => bulkSetItemCompliant(data),
  })
}

export const useBulkSetItemNonInfringing = () => {
  return useMutation({
    mutationFn: (data: BulkSetNonInfringingPayload) => bulkSetItemNonInfringing(data),
  })
}

export const useBulkSetItemManualNtdReminder = () => {
  return useMutation({
    mutationFn: (data: BulkItemIds) => bulkSetItemManualNtdReminder(data),
  })
}

export const useSetItemUnderInvestigationBulk = () => {
  return useMutation({
    mutationFn: (data: BulkCreateInvestigationPayload) => setItemUnderInvestigationBulk(data),
  })
}

export const useAddBookmarkItems = () => {
  return useMutation({
    mutationFn: (data: { item_ids: string[] }) => addBookmarkItems(data),
  })
}

export const useRemoveBookmarkItems = () => {
  return useMutation({
    mutationFn: (data: { item_ids: string[] }) => removeBookmarkItems(data),
  })
}

export const useInvalidateItemsAll = () => {
  const qc = useQueryClient()
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: ['items'] })
    qc.invalidateQueries({ queryKey: ['item'] })
  }, [qc])
}

export const useInvalidateItemCommentHistory = () => {
  const qc = useQueryClient()
  return useCallback((itemId: string) => qc.invalidateQueries({ queryKey: ITEM_KEYS.comments(itemId) }), [qc])
}

export const useInvalidateFilteredItems = () => {
  const qc = useQueryClient()
  return useCallback(() => qc.invalidateQueries({ queryKey: ['items'] }), [qc])
}
